import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { post, put } from "~/Services";
import { passengerHost } from "~/Utils";
import { fields } from "../constants";
import { useHomepageBannerDetails } from "./Slider.hooks";

const AddEditSlider = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const [currentAirport] = useLocalStorage("currentAirport", null);
  const {
    data: editHomepageBanner,
    error,
    isLoading: homepageBannerLoading,
    isError,
    refetch: refetchHomepageBanner,
  } = useHomepageBannerDetails(query.get("editId") || "", currentAirport);

  const {
    data: editHomepageBannerById,
    isLoading: isLoadingDocument,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchHomepageBanner();
    }
  }, [query.get("editId"), query.get("id")]);

  const homepageBannerFormConfig: FormConfig = {
    initialValues: {
      title: decodeHTML(editHomepageBannerById?.payload?.title) || decodeHTML(editHomepageBanner?.title) || "",
      redirectedURL:
        decodeHTML(editHomepageBannerById?.payload?.redirectedURL) ||
        decodeHTML(editHomepageBanner?.redirectedURL) ||
        "",
      sequence: decodeHTML(editHomepageBannerById?.payload?.sequence) || decodeHTML(editHomepageBanner?.sequence),
      published: editHomepageBannerById?.payload?.published ?? editHomepageBanner?.published ?? false,
      banner_image: editHomepageBannerById?.payload
        ? [
            {
              uid: "-1",
              name: "banner.jpg",
              status: "done",
              url: decodeHTML(editHomepageBannerById?.payload?.banner_image),
            },
          ]
        : editHomepageBanner
          ? [
              {
                uid: "-1",
                name: "banner.jpg",
                status: "done",
                url: decodeHTML(editHomepageBanner?.banner_image),
              },
            ]
          : "",
    },
    fields: fields.sliders,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: string[] = ["banner_image"];

    for (const key of imageKeys) {
      try {
        const field = data[key];
        if (field[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "PASSENGER");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {
        console.error("Image upload failed", error);
      }
    }

    const payload = {
      action,
      method,
      module: "Home Page",
      subModule: "sliders",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [
        {
          appName: "PASSENGER_ECMS",
          apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && {
          id: editHomepageBanner?.id || editHomepageBannerById?.payload?.id,
        }),
        ...data,
      },
      status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status === "PENDING" ? "Data sent for review" : "Data saved successfully",
            type: "success",
          });
          if (response.status === "OK" && status === "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status === "DRAFT" ? "Data saved successfully" : "Data sent for review",
            type: "success",
          });
          if (response.status === "OK" && status === "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };
  const getPatchHomepageBannerApiUrl = (airport: string, id: string | number) => {
    switch (airport?.toLowerCase()) {
      case "klia1":
        return `${passengerHost}/api/homepage-banners/${id}`;

      case "klia2":
        return `${passengerHost}/api/homepage-banner-klia2-crud-data/${id}`;

      case "penang":
        return `${passengerHost}/api/homepage-banner-pen-crud-data/${id}`;

      case "kota-kinabalu":
        return `${passengerHost}/api/homepage-banner-bk-crud-data/${id}`;

      case "kuching":
        return `${passengerHost}/api/homepage-banner-kch-crud-data/${id}`;

      case "langkawi":
        return `${passengerHost}/api/homepage-banner-lgk-crud-data/${id}`;

      default:
        return "";
    }
  };
  const getPostHomepageBannerApiUrl = (airport: string) => {
    switch (airport?.toLowerCase()) {
      case "klia1":
        return `${passengerHost}/api/homepage-banners`;

      case "klia2":
        return `${passengerHost}/api/create-homepage-banner-klia2-crud-data`;

      case "penang":
        return `${passengerHost}/api/create-homepage-banner-pen-crud-data`;

      case "kota-kinabalu":
        return `${passengerHost}/api/create-homepage-banner-bk-crud-data`;

      case "kuching":
        return `${passengerHost}/api/create-homepage-banner-kch-crud-data`;

      case "langkawi":
        return `${passengerHost}/api/create-homepage-banner-lgk-crud-data`;

      default:
        return "";
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Slider`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingDocument || homepageBannerLoading}
        config={homepageBannerFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${
              query.get("editId") || query.get("type") === "update"
                ? getPatchHomepageBannerApiUrl(
                    currentAirport,
                    editHomepageBanner?.id || editHomepageBannerById?.payload?.id
                  )
                : getPostHomepageBannerApiUrl(currentAirport)
            }`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Homepage"
      />
    </ContentWrapper>
  );
};

export default AddEditSlider;
